import { LinkIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/ThemeContext'
import { withAlpha } from '../../utils/themeUtils'
import { Button } from '../ui/button'
import { Input } from '../ui/input'

type CaptureLinkInputProps = {
  active: boolean
  value: string
  error?: string | null
  disabled?: boolean
  onAdd: () => void
  onChange: (value: string) => void
  onRemove: () => void
}

export function CaptureLinkInput({
  active,
  value,
  error,
  disabled,
  onAdd,
  onChange,
  onRemove,
}: CaptureLinkInputProps) {
  const { t } = useTranslation()
  const { colors } = useTheme()

  if (!active) {
    return (
      <Button
        type="button"
        variant="ghost"
        disabled={disabled}
        onClick={onAdd}
        className="min-h-11 rounded-xl px-3 text-sm font-semibold"
        style={{ color: colors.primary, backgroundColor: withAlpha(colors.primary, '10') }}
      >
        <LinkIcon className="h-5 w-5" />
        {t('core.capture.link.add')}
      </Button>
    )
  }

  return (
    <div
      className="rounded-2xl border p-3"
      style={{ borderColor: withAlpha(error ? colors.error : colors.border, 'B0') }}
    >
      <div className="flex items-center gap-2">
        <LinkIcon className="h-5 w-5 shrink-0" style={{ color: colors.primary }} />
        <Input
          type="url"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t('core.capture.link.placeholder')}
          aria-label={t('core.capture.link.inputLabel')}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'capture-link-error' : undefined}
          className="min-w-0 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={onRemove}
          aria-label={t('core.capture.link.remove')}
          className="shrink-0"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
      </div>
      {error ? (
        <p id="capture-link-error" role="alert" className="mt-2 text-xs" style={{ color: colors.error }}>
          {error}
        </p>
      ) : (
        <p className="mt-2 text-xs" style={{ color: colors.textSecondary }}>
          {t('core.capture.link.help')}
        </p>
      )}
    </div>
  )
}
